import { useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router";
import { Button } from "../../components/ui/Button";
import { FormError } from "../../components/ui/Banner";
import { useAction } from "../../hooks/useAction";
import { authApi } from "../../lib/api/auth";
import { isApiError } from "../../lib/api/http";
import { useSession } from "../../app/session";
import { AuthLayout } from "./AuthLayout";
import { CodeField, EmailField, PasswordField } from "./fields";
import { ResendButton } from "./ResendButton";
import { VerifyEmail } from "./VerifyEmail";

type Step = "password" | "unverified" | "code-email" | "code";

/** Signing in by password or by an emailed code. Once the session exists, GuestOnly sends the user on. */
export function SignInPage() {
  const { refresh } = useSession();
  const { search } = useLocation();
  const [step, setStep] = useState<Step>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");

  const signIn = useAction(async () => {
    try {
      await authApi.signIn(email, password);
    } catch (e) {
      if (isApiError(e) && e.status === 403) {
        await authApi.sendCode(email, "email-verification");
        setStep("unverified");
        return;
      }
      throw e;
    }
    await refresh();
  });
  const sendCode = useAction(async () => { await authApi.sendCode(email, "sign-in"); setStep("code"); });
  const codeSignIn = useAction(async () => { await authApi.signInWithCode(email, otp); await refresh(); });

  const footer = (
    <>
      <Link to={`/sign-up${search}`}>create account</Link>
      <Link to="/reset-password">forgot password?</Link>
    </>
  );

  if (step === "unverified") {
    return (
      <AuthLayout title="Confirm your email">
        <VerifyEmail email={email} onVerified={async () => { await authApi.signIn(email, password); await refresh(); }} />
      </AuthLayout>
    );
  }

  if (step === "code-email" || step === "code") {
    const submit = (e: FormEvent) => { e.preventDefault(); void (step === "code" ? codeSignIn.run() : sendCode.run()); };
    return (
      <AuthLayout title="Sign in with a code" footer={footer}>
        <form onSubmit={submit} className="flex flex-col gap-4.5">
          {step === "code-email"
            ? <EmailField value={email} onChange={setEmail} autoFocus />
            : <CodeField email={email} value={otp} onChange={setOtp} />}
          <FormError message={sendCode.error ?? codeSignIn.error} />
          <Button type="submit" variant="primary" pending={sendCode.pending || codeSignIn.pending}>
            {step === "code" ? "sign in →" : "email me a code →"}
          </Button>
          {step === "code" && <ResendButton onResend={() => authApi.sendCode(email, "sign-in")} />}
          <Button variant="ghost" size="sm" onClick={() => { setStep("password"); setOtp(""); }}>use my password instead</Button>
        </form>
      </AuthLayout>
    );
  }

  const submit = (e: FormEvent) => { e.preventDefault(); void signIn.run(); };
  return (
    <AuthLayout title="Sign in" footer={footer}>
      <form onSubmit={submit} className="flex flex-col gap-4.5">
        <EmailField value={email} onChange={setEmail} autoFocus />
        <PasswordField value={password} onChange={setPassword} />
        <FormError message={signIn.error} />
        <Button type="submit" variant="primary" pending={signIn.pending} pendingLabel="signing in…">sign in →</Button>
        <Button variant="ghost" size="sm" onClick={() => setStep("code-email")}>email me a code instead</Button>
      </form>
    </AuthLayout>
  );
}
